import React, { useRef, useMemo, Suspense } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";
import styled from "styled-components";

const CanvasWrapper = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
  overflow: hidden;
  opacity: 0.75;
`;

function ParticleStarfield({ count = 350 }) {
  const pointsRef = useRef();

  const [positions, colors] = useMemo(() => {
    const pos = new Float32Array(count * 3);
    const col = new Float32Array(count * 3);
    const cyan = new THREE.Color("#38bdf8");
    const purple = new THREE.Color("#818cf8");
    const white = new THREE.Color("#ffffff");

    for (let i = 0; i < count; i++) {
      pos[i * 3] = (Math.random() - 0.5) * 22;
      pos[i * 3 + 1] = (Math.random() - 0.5) * 18;
      pos[i * 3 + 2] = (Math.random() - 0.5) * 16;

      const rand = Math.random();
      const mixed = rand < 0.55 ? cyan : rand < 0.85 ? purple : white;
      col[i * 3] = mixed.r;
      col[i * 3 + 1] = mixed.g;
      col[i * 3 + 2] = mixed.b;
    }
    return [pos, col];
  }, [count]);

  useFrame((state, delta) => {
    if (pointsRef.current) {
      pointsRef.current.rotation.y += delta * 0.025;
      pointsRef.current.rotation.x += delta * 0.01;

      // Subtle mouse reaction
      const targetX = state.pointer.x * 0.3;
      const targetY = state.pointer.y * 0.2;
      pointsRef.current.position.x = THREE.MathUtils.damp(
        pointsRef.current.position.x,
        targetX,
        2.5,
        delta
      );
      pointsRef.current.position.y = THREE.MathUtils.damp(
        pointsRef.current.position.y,
        targetY,
        2.5,
        delta
      );
    }
  });

  return (
    <points ref={pointsRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={positions.length / 3}
          array={positions}
          itemSize={3}
        />
        <bufferAttribute
          attach="attributes-color"
          count={colors.length / 3}
          array={colors}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial
        size={0.065}
        vertexColors
        transparent
        opacity={0.8}
        blending={THREE.AdditiveBlending}
        sizeAttenuation
      />
    </points>
  );
}

const AmbientHeroCanvas = () => {
  return (
    <CanvasWrapper>
      <Suspense fallback={null}>
        <Canvas
          camera={{ position: [0, 0, 7], fov: 45 }}
          dpr={[1, 1.5]}
          gl={{
            antialias: true,
            alpha: true,
            powerPreference: "high-performance"
          }}
        >
          <ambientLight intensity={0.5} />
          <ParticleStarfield count={350} />
        </Canvas>
      </Suspense>
    </CanvasWrapper>
  );
};

export default AmbientHeroCanvas;
